/*
 * Carbisdale - Cursor-Modul
 *
 * Verwaltet den blinkenden Block-Cursor.
 * Der Cursor ist immer an genau einer Position sichtbar
 * und invertiert das Zeichen darunter beim Blinken.
 *
 * Verantwortlichkeiten:
 * - Cursor-Position verwalten
 * - Blink-State (sichtbar/unsichtbar) umschalten
 * - Bounds-Checking gegen Screen-Dimensionen
 */

import { TIMING, INITIAL_CURSOR } from './config.js'

/**
 * Cursor-Klasse
 *
 * Der Terminal-Cursor blinkt mit einem festen Intervall
 * und invertiert dabei das Zeichen an seiner Position.
 */
export class Cursor {
    /**
     * Erstellt einen neuen Cursor.
     *
     * @param {number} maxCols - Maximale Spaltenanzahl (für Bounds-Check)
     * @param {number} maxRows - Maximale Zeilenanzahl (für Bounds-Check)
     */
    constructor(maxCols, maxRows) {
        this.x = INITIAL_CURSOR.x
        this.y = INITIAL_CURSOR.y
        this.maxCols = maxCols
        this.maxRows = maxRows

        // Blink-State
        this.visible = true                     // Aktueller Zustand (an/aus)
        this.blinkInterval = TIMING.cursorBlink // Intervall in ms
        this._intervalId = null                 // Timer-ID für Cleanup
        this.blinkDirty = false                 // Nur Cursor-Layer muss neu gezeichnet werden

        // Bounds-Check für initiale Position
        this._clampPosition()
    }

    /**
     * Startet das Cursor-Blinken.
     *
     * @param {Function} onBlink - Callback bei jedem Blink (für Dirty-Flag)
     */
    startBlinking(onBlink) {
        // Erst stoppen falls bereits aktiv
        this.stopBlinking()

        this._intervalId = setInterval(() => {
            this.visible = !this.visible

            // Nur Cursor-Layer als dirty markieren (kein Full Redraw)
            this.blinkDirty = true

            // Legacy-Callback für Fälle die Full Redraw brauchen
            if (onBlink) {
                onBlink()
            }
        }, this.blinkInterval)
    }

    /**
     * Stoppt das Cursor-Blinken.
     */
    stopBlinking() {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId)
            this._intervalId = null
        }
    }

    /**
     * Setzt die Cursor-Position.
     * Wird automatisch auf gültige Bounds begrenzt.
     *
     * @param {number} x - Neue Spalten-Position
     * @param {number} y - Neue Zeilen-Position
     */
    setPosition(x, y) {
        this.x = x
        this.y = y
        this._clampPosition()

        // Cursor sichtbar machen bei Bewegung (typisches Terminal-Verhalten)
        this.visible = true
    }

    /**
     * Bewegt den Cursor relativ zur aktuellen Position.
     *
     * @param {number} dx - Verschiebung in X-Richtung
     * @param {number} dy - Verschiebung in Y-Richtung
     */
    move(dx, dy) {
        this.setPosition(this.x + dx, this.y + dy)
    }

    /**
     * Aktualisiert die maximalen Dimensionen (z.B. nach Resize).
     * Passt die Position an falls nötig.
     *
     * @param {number} maxCols - Neue maximale Spaltenanzahl
     * @param {number} maxRows - Neue maximale Zeilenanzahl
     */
    updateBounds(maxCols, maxRows) {
        this.maxCols = maxCols
        this.maxRows = maxRows
        this._clampPosition()
    }

    /**
     * Begrenzt die Position auf gültige Werte.
     * Wird intern aufgerufen bei Position-Änderungen.
     */
    _clampPosition() {
        // Minimum ist 0
        this.x = Math.max(0, this.x)
        this.y = Math.max(0, this.y)

        // Maximum ist cols-1 bzw. rows-1
        this.x = Math.min(this.x, this.maxCols - 1)
        this.y = Math.min(this.y, this.maxRows - 1)
    }

    /**
     * Prüft ob der Cursor an einer bestimmten Position ist.
     * Nützlich beim Rendern um zu wissen ob invertiert werden muss.
     *
     * @param {number} x - Zu prüfende Spalte
     * @param {number} y - Zu prüfende Zeile
     * @returns {boolean}
     */
    isAt(x, y) {
        return this.x === x && this.y === y
    }

    /**
     * Cleanup - stoppt das Blinken.
     * Sollte aufgerufen werden wenn der Cursor nicht mehr gebraucht wird.
     */
    destroy() {
        this.stopBlinking()
    }
}
